import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { isMobile } from 'hooks';
import { capitalize } from 'utils/string';
import { MinusPayoutTypes } from 'store/api/payout';
import { useEmployeeEventsQuery } from 'store/api/event';
import { formatPrice, joinStrings } from 'utils/helpers';
import {
  PayoutType,
  ButtonColors,
  ExpensesType,
  ButtonStyleTypes,
} from 'types';
import {
  Flex,
  Icon,
  Dialog,
  Button,
  InfoBlock,
  ButtonTypes,
  CommonStyles,
  ErrorMessage,
  CreateBonusAdvanceDialog,
} from 'components';

import * as Styles from './styles';
import { PayoutInfo } from './payout-info';
import { PayoutDialogProps } from './types';
import { TotalInfoCard } from './total-info-card';
import { Card, Price, AdjustmentCard } from './card';
import { splitNegativeItems, negativeSplitTypes } from './helpers';
import {
  additions,
  deductions,
  payoutTitles,
  signedAmountTypes,
} from './config';

export const PayoutDialog = ({
  onClose,
  eventId,
  onSubmit,
  hideButtons,
  selectedValues,
  isDisableEvent,
  setSelectedValues,
}: PayoutDialogProps) => {
  const mobile = isMobile();
  const { id } = useParams();
  const isSeveral = selectedValues?.length > 1;
  const employee = selectedValues?.[0]?.employee;
  const [payout, setPayout] = useState(
    isSeveral ? undefined : selectedValues[0]
  );

  const { data: employeeEvents } = useEmployeeEventsQuery(Number(id!), {
    skip: !id,
  });

  const payPrice = selectedValues.reduce((acc, val) => {
    const amount = Number(val.amount || 0);

    if (signedAmountTypes.includes(val.type)) {
      return acc + amount;
    }

    return MinusPayoutTypes.includes(val.type)
      ? acc - Math.abs(amount)
      : acc + Math.abs(amount);
  }, 0);

  const totalPrice = formatPrice(payPrice);
  const isPayrollsNegative = payPrice <= 0;

  const { additionValues, deductionValues } = useMemo(
    () => ({
      deductionValues: selectedValues.filter((value) => {
        if (signedAmountTypes.includes(value.type)) {
          return Number(value.amount || 0) < 0;
        }
        return MinusPayoutTypes.includes(value.type);
      }),
      additionValues: selectedValues.filter((value) => {
        if (signedAmountTypes.includes(value.type)) {
          return Number(value.amount || 0) > 0;
        }
        return !MinusPayoutTypes.includes(value.type);
      }),
    }),
    [selectedValues]
  );

  const getCustomTypeValues = (isDeduction?: boolean) =>
    Object.values(ExpensesType).filter((type) =>
      isDeduction ? deductions.includes(type) : additions.includes(type)
    );

  const getBlock = (isDeduction?: boolean) => {
    const baseTypes = isDeduction
      ? MinusPayoutTypes
      : Object.values(PayoutType).filter(
          (value) => !MinusPayoutTypes.includes(value)
        );

    const uniqueTypes = Array.from(
      new Set([...baseTypes, ...signedAmountTypes])
    );

    const { refundItems, chargebackItems } = isDeduction
      ? splitNegativeItems(deductionValues)
      : { refundItems: [], chargebackItems: [] };

    // Payroll settings: the automatic broken & missing line is its own row,
    // never folded into Misc.
    const isBrokenMissing = (value: { payrollRule?: string }) =>
      value.payrollRule === 'brokenMissing';
    const blockValues = (isDeduction ? deductionValues : additionValues).filter(
      (value) => !isBrokenMissing(value)
    );
    const brokenMissingTotal = (isDeduction ? deductionValues : additionValues)
      .filter(isBrokenMissing)
      .reduce((sum, value) => sum + Number(value.amount || 0), 0);

    return (
      <InfoBlock>
        <Flex gap={16} flexDirection="column">
          <Styles.Title>
            {isDeduction ? 'Deductions' : 'Additions'}
          </Styles.Title>

          {uniqueTypes.map((value) => {
            if (
              isDeduction &&
              negativeSplitTypes.includes(value as PayoutType)
            ) {
              return null;
            }

            return (
              <TotalInfoCard
                key={value}
                type={value}
                isDeduction={isDeduction}
                selectedValues={blockValues}
                title={capitalize(payoutTitles[value] || value)}
              />
            );
          })}

          {brokenMissingTotal !== 0 && (
            <Card
              isDeduction={isDeduction}
              title="Broken & missing products"
              value={`${isDeduction ? '-' : ''}${formatPrice(Math.abs(brokenMissingTotal))}`}
            />
          )}

          {isDeduction && (
            <>
              <AdjustmentCard title="Refund" items={refundItems} />
              <AdjustmentCard title="Chargeback" items={chargebackItems} />
            </>
          )}
        </Flex>

        {!hideButtons && (
          <CreateBonusAdvanceDialog
            withReceipt
            isWideDialog
            eventId={eventId}
            hideCashFromEvent
            customTitle="Add row"
            showSelectExpensesType
            employeeId={String(id)}
            customEvents={employeeEvents}
            isDisableEvent={isDisableEvent}
            customTypeValues={getCustomTypeValues(isDeduction)}
            customEmployeeOptions={[
              {
                value: String(employee?.id),
                label: String(employee?.user?.name),
              },
            ]}
            onSuccessSubmit={(values) =>
              setSelectedValues?.((prevValues: number[]) => [
                ...prevValues,
                values.id,
              ])
            }
            trigger={
              <Styles.AddRowButton
                transparent
                padding="0"
                text="Add row"
                icon={<Icon.Plus />}
                colorType={ButtonColors.LightBlue}
              />
            }
          />
        )}
      </InfoBlock>
    );
  };

  const allPayoutInfo = (
    <Flex gap={16} flexDirection="column">
      <Styles.Title>
        Events:{' '}
        <span className="semibold">
          {joinStrings(
            Array.from(
              new Set(selectedValues.map((value) => value?.event?.name))
            ),
            ', '
          )}
        </span>
      </Styles.Title>

      {additionValues.length > 0 && getBlock()}
      {deductionValues.length > 0 && getBlock(true)}
    </Flex>
  );

  const payoutInfo =
    isSeveral && !payout ? (
      <Flex gap={8} fullWidth flexDirection="column">
        {allPayoutInfo}

        <Price payout={payout} totalPrice={totalPrice} />
      </Flex>
    ) : (
      <PayoutInfo
        payout={payout}
        totalPrice={totalPrice}
        showPayoutPrice={isSeveral}
        onBackClick={isSeveral ? () => setPayout(undefined) : undefined}
      />
    );

  return (
    <Dialog
      open
      onClose={onClose}
      width={mobile ? '90vw' : '520px'}
      closeOnDocumentClick={Boolean(hideButtons)}
      title={isSeveral ? 'Pay several payrolls' : `Payroll ${payout?.id}`}
    >
      {(close) =>
        onSubmit ? (
          <Formik initialValues={{}} onSubmit={onSubmit}>
            {({ isSubmitting }) => (
              <CommonStyles.Form gap={24}>
                {payoutInfo}

                {isPayrollsNegative && (
                  <ErrorMessage>
                    The total must be greater than zero
                  </ErrorMessage>
                )}
                {!hideButtons && (
                  <Flex gap={16} fullWidth>
                    <Button
                      fullWidth
                      text="Cancel"
                      onClick={close}
                      styleType={ButtonStyleTypes.Outline}
                    />
                    <Button
                      fullWidth
                      type={ButtonTypes.Submit}
                      text={`Pay (${totalPrice})`}
                      disabled={isSubmitting || isPayrollsNegative}
                    />
                  </Flex>
                )}
              </CommonStyles.Form>
            )}
          </Formik>
        ) : (
          payoutInfo
        )
      }
    </Dialog>
  );
};
